import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { Text } from '@/components/ui';
import { useApprovalQueue } from '@/hooks/use-approval-queue';

/**
 * The unmissable one: work orders past their two-business-day approval window (#496).
 *
 * ── WHY IT RIDES EVERY PAGE ──
 *
 * Mounted in the authenticated shell rather than on the dashboard, because a property
 * manager who lands on a work order from an email link, or who lives in the inbox all
 * morning, never sees the dashboard card. The whole reason this feature exists is that
 * approvals were getting missed; a reminder that only appears where someone already went
 * looking reproduces the problem it replaces.
 *
 * ── OVERDUE ONLY ──
 *
 * Deliberately silent while the SLA is still running. Routine pending work belongs on the
 * dashboard card; a banner that is always up is furniture, and furniture gets ignored — at
 * which point it is no longer available for the case that matters.
 *
 * Renders for whoever OWES the approvals, which `useApprovalQueue` resolves — a manager,
 * or an admin for the associations THEY are assigned to. Accounting never sees it: their
 * queue is reviewing, which carries no clock. Nobody is ever alarmed about a work order
 * they cannot be the one to approve.
 *
 * No print-route guard needed: `app/_layout.tsx` returns a separate tree for `/print/**`
 * that does not mount the shell banners at all, so this can never land on paper.
 */

const OVERDUE = '#c62828';

/** Where the banner would send someone — no point pointing at the page they are on. */
const TARGET = '/';

export const ApprovalOverdueBanner: React.FC = () => {
	/**
	 * The QUEUE decides everything — no role check.
	 *
	 * `useApprovalQueue` already returns the right queue for this user, and accounting's
	 * needs-review rows carry no SLA, so their `overdueCount` is zero and the banner stays
	 * down without a second rule saying so. That also covers an admin who manages
	 * associations — they owe those approvals and were once excluded by a
	 * `role === PropertyManager` test.
	 *
	 * The scope that keeps it HONEST lives in the hook too (`personal: true`). Do not be
	 * tempted to add a `role !== Admin` guard here: an admin who manages associations owes
	 * real approvals and must still be told. The rule is ownership, not rank — an admin was
	 * seeing this bar for a work order on another manager's association, which no action of
	 * theirs could clear.
	 *
	 * Reads the same hook as the tile, the badge and the To Do panel, so this banner can
	 * never claim a different number from the screen it links to. The query key is identical
	 * to the dashboard's, so TanStack serves both from one request.
	 */
	const { overdueCount } = useApprovalQueue();

	if (overdueCount === 0) return null;

	return (
		<Pressable
			onPress={() => router.push(TARGET)}
			style={styles.banner}
			testID="approval-overdue-banner"
			accessibilityRole="button"
			accessibilityLabel={`${overdueCount} work orders are overdue for your approval. Tap to review.`}
		>
			<Ionicons name="alert-circle" size={22} color="#fff" />
			<View style={styles.textBlock}>
				<Text style={styles.title}>
					{overdueCount} work order{overdueCount === 1 ? ' is' : 's are'} overdue for your approval
				</Text>
				<Text style={styles.subtitle}>
					Accounting is waiting on {overdueCount === 1 ? 'this' : 'these'} to invoice the association.
					Tap to review.
				</Text>
			</View>
			<Ionicons name="chevron-forward" size={20} color="#fff" />
		</Pressable>
	);
};

const styles = StyleSheet.create({
	banner: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 12,
		backgroundColor: OVERDUE,
		paddingVertical: 12,
		paddingHorizontal: 16,
	},
	textBlock: {
		flex: 1,
		// RN-Web flex children default to min-width:auto, so without this the long
		// subtitle pushes the chevron off the edge instead of wrapping.
		minWidth: 0,
	},
	title: {
		color: '#fff',
		fontSize: 16,
		fontWeight: '700',
	},
	subtitle: {
		color: 'rgba(255,255,255,0.9)',
		fontSize: 13,
		marginTop: 2,
	},
});
